import { useState } from 'react';
import { QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query';

const API_URL = import.meta.env.VITE_API_URL || 'https://api.frolic.so';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60,
      retry: 1,
    },
  },
});

interface LogEntry {
  eventId: string;
  eventTime: string;
  level: string;
  message: string;
  service?: string;
  environment?: string;
  traceId?: string;
  attributes?: Record<string, unknown>;
}

interface QueryResponse {
  logs: LogEntry[];
  total: number;
  hasMore: boolean;
  cursor?: string;
  bytesScanned: number;
}

const LogViewer = () => {
  const [projectId, setProjectId] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [timeRange, setTimeRange] = useState('1h');
  const [levelFilter, setLevelFilter] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');

  const getTimeRange = () => {
    const to = new Date();
    const from = new Date();
    switch (timeRange) {
      case '15m':
        from.setMinutes(from.getMinutes() - 15);
        break;
      case '1h':
        from.setHours(from.getHours() - 1);
        break;
      case '6h':
        from.setHours(from.getHours() - 6);
        break;
      case '24h':
        from.setHours(from.getHours() - 24);
        break;
      default:
        from.setHours(from.getHours() - 1);
    }
    return { from: from.toISOString(), to: to.toISOString() };
  };

  const { data, isLoading, error, refetch } = useQuery<QueryResponse>({
    queryKey: ['logs', projectId, timeRange, levelFilter, searchQuery],
    queryFn: async () => {
      if (!projectId || !apiKey) throw new Error('Project ID and API key required');
      const { from, to } = getTimeRange();
      const response = await fetch(`${API_URL}/v1/query`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${apiKey}`,
        },
        body: JSON.stringify({
          projectId,
          from,
          to,
          level: levelFilter.length > 0 ? levelFilter : undefined,
          query: searchQuery || undefined,
          limit: 100,
        }),
      });
      if (!response.ok) throw new Error('Query failed');
      return response.json();
    },
    enabled: !!projectId && !!apiKey,
    refetchInterval: 30000,
  });

  const getLevelColor = (level: string) => {
    switch (level) {
      case 'error':
      case 'fatal':
        return '#ef4444';
      case 'warn':
        return '#f59e0b';
      case 'info':
        return '#3b82f6';
      case 'debug':
        return '#6b7280';
      default:
        return '#9ca3af';
    }
  };

  return (
    <div style={{ padding: '20px', fontFamily: 'system-ui, sans-serif' }}>
      <h1 style={{ margin: '0 0 20px' }}>Frolic Analytics</h1>

      <div style={{ display: 'flex', gap: '10px', marginBottom: '20px', flexWrap: 'wrap' }}>
        <input
          type="text"
          placeholder="Project ID"
          value={projectId}
          onChange={(e) => setProjectId(e.target.value)}
          style={{ padding: '8px', border: '1px solid #ccc', borderRadius: '4px', width: '200px' }}
        />
        <input
          type="password"
          placeholder="API Key"
          value={apiKey}
          onChange={(e) => setApiKey(e.target.value)}
          style={{ padding: '8px', border: '1px solid #ccc', borderRadius: '4px', width: '300px' }}
        />
        <select
          value={timeRange}
          onChange={(e) => setTimeRange(e.target.value)}
          style={{ padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
        >
          <option value="15m">Last 15 minutes</option>
          <option value="1h">Last 1 hour</option>
          <option value="6h">Last 6 hours</option>
          <option value="24h">Last 24 hours</option>
        </select>
        <input
          type="text"
          placeholder="Search logs..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={{ padding: '8px', border: '1px solid #ccc', borderRadius: '4px', width: '200px' }}
        />
        <button
          onClick={() => refetch()}
          style={{
            padding: '8px 16px',
            background: '#3b82f6',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
          }}
        >
          Search
        </button>
      </div>

      <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
        {['debug', 'info', 'warn', 'error'].map((level) => (
          <label key={level} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <input
              type="checkbox"
              checked={levelFilter.includes(level)}
              onChange={(e) => {
                if (e.target.checked) {
                  setLevelFilter([...levelFilter, level]);
                } else {
                  setLevelFilter(levelFilter.filter((l) => l !== level));
                }
              }}
            />
            <span style={{ color: getLevelColor(level), fontWeight: 'bold' }}>{level.toUpperCase()}</span>
          </label>
        ))}
      </div>

      {isLoading && <p>Loading...</p>}
      {error && <p style={{ color: 'red' }}>Error: {(error as Error).message}</p>}

      {data && (
        <div>
          <p style={{ color: '#666', marginBottom: '10px' }}>
            Found {data.total} logs ({(data.bytesScanned / 1024).toFixed(1)} KB scanned)
            {data.hasMore && ' - more available'}
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            {data.logs.map((log) => (
              <div
                key={log.eventId}
                style={{
                  padding: '8px 12px',
                  background: '#f9fafb',
                  borderLeft: `3px solid ${getLevelColor(log.level)}`,
                  borderRadius: '4px',
                  fontFamily: 'monospace',
                  fontSize: '13px',
                }}
              >
                <div style={{ display: 'flex', gap: '10px', marginBottom: '4px' }}>
                  <span style={{ color: '#666' }}>{new Date(log.eventTime).toLocaleTimeString()}</span>
                  <span style={{ color: getLevelColor(log.level), fontWeight: 'bold' }}>
                    {log.level.toUpperCase()}
                  </span>
                  {log.service && <span style={{ color: '#8b5cf6' }}>[{log.service}]</span>}
                </div>
                <div>{log.message}</div>
                {log.traceId && (
                  <div style={{ color: '#666', fontSize: '11px', marginTop: '4px' }}>trace: {log.traceId}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {!projectId && !apiKey && (
        <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
          <p>Enter your Project ID and API key to view logs</p>
          <p style={{ fontSize: '14px' }}>
            Get your credentials from{' '}
            <a href="https://docs.frolic.so" style={{ color: '#3b82f6' }}>
              docs.frolic.so
            </a>
          </p>
        </div>
      )}
    </div>
  );
};

export const App = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <LogViewer />
    </QueryClientProvider>
  );
};
